import { roundRating } from './averageRating';

export type CourierLicenseTier = {
  level: number;
  nameKey: string;
  minSuccessfulDeliveries: number;
  minAverageRating: number;
};

/** Пороги лицензии курьера из GDD v2 (успешные доставки + мин. средний рейтинг). */
export const COURIER_LICENSE_TIERS: readonly CourierLicenseTier[] = [
  { level: 1, nameKey: 'game.license.tier1', minSuccessfulDeliveries: 0, minAverageRating: 0 },
  { level: 2, nameKey: 'game.license.tier2', minSuccessfulDeliveries: 3, minAverageRating: 1.4 },
  { level: 3, nameKey: 'game.license.tier3', minSuccessfulDeliveries: 5, minAverageRating: 2.2 },
  { level: 4, nameKey: 'game.license.tier4', minSuccessfulDeliveries: 8, minAverageRating: 2.9 },
  { level: 5, nameKey: 'game.license.tier5', minSuccessfulDeliveries: 11, minAverageRating: 3.4 },
  { level: 6, nameKey: 'game.license.tier6', minSuccessfulDeliveries: 15, minAverageRating: 3.8 },
  { level: 7, nameKey: 'game.license.tier7', minSuccessfulDeliveries: 19, minAverageRating: 4.1 },
  { level: 8, nameKey: 'game.license.tier8', minSuccessfulDeliveries: 23, minAverageRating: 4.4 },
  { level: 9, nameKey: 'game.license.tier9', minSuccessfulDeliveries: 28, minAverageRating: 4.6 },
  { level: 10, nameKey: 'game.license.tier10', minSuccessfulDeliveries: 32, minAverageRating: 4.8 },
] as const;

export const COURIER_LICENSE_MAX_LEVEL = COURIER_LICENSE_TIERS.length;

/** Ключ i18n для названия лицензии (1 — стартовая «Тучка на побегушках»). */
export function getCourierLicenseNameKey(level: number): string {
  if (level <= 1) {
    return 'game.license.tier1';
  }
  return COURIER_LICENSE_TIERS[level - 1]?.nameKey ?? 'game.license.tier1';
}

/** Текущий уровень лицензии по числу успешных доставок и среднему рейтингу. */
export function getCourierLicenseLevel(
  successfulDeliveries: number,
  averageRating: number,
): number {
  let level = 0;
  for (const tier of COURIER_LICENSE_TIERS) {
    if (
      successfulDeliveries >= tier.minSuccessfulDeliveries &&
      averageRating >= tier.minAverageRating
    ) {
      level = tier.level;
    } else {
      break;
    }
  }
  return level;
}

export function getCourierLicenseProgressToNext(
  successfulDeliveries: number,
  averageRating: number,
): {
  currentLevel: number;
  deliveriesToNext: number;
  ratingToNext: number;
  isMaxLevel: boolean;
} {
  const currentLevel = getCourierLicenseLevel(successfulDeliveries, averageRating);
  if (currentLevel >= COURIER_LICENSE_MAX_LEVEL) {
    return { currentLevel, deliveriesToNext: 0, ratingToNext: 0, isMaxLevel: true };
  }
  const nextTier = COURIER_LICENSE_TIERS[currentLevel];
  return {
    currentLevel,
    deliveriesToNext: Math.max(0, nextTier.minSuccessfulDeliveries - successfulDeliveries),
    ratingToNext: roundRating(Math.max(0, nextTier.minAverageRating - averageRating)),
    isMaxLevel: false,
  };
}
